import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSessionToken, useSession } from "../hooks/useSession";
import { ReferralLinksPanel } from "./ReferralLinksPanel";
import {
  Card,
  EmptyState,
  Skeleton,
  SkeletonText,
  StatusBadge,
  LoadingStatus,
} from "./ui";
import { PortalPageIntro, PortalWatermarkIcon } from "./portal/PortalVisuals";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface DownlineRow {
  id: string;
  email: string;
  displayName: string | null;
  joinedAt: string;
  commissionLevel: string | null;
  isRegistered: boolean;
  contractStats: {
    total: number;
    active: number;
    pending: number;
    nigo: number;
    rejected: number;
  };
}

function RegisteredIndicator({ registered }: { registered: boolean }) {
  return (
    <span
      className="inline-flex items-center justify-center"
      title={registered ? "Registered in agent portal" : "Not registered in agent portal"}
      aria-label={registered ? "Registered" : "Not registered"}
    >
      {registered ? (
        <svg
          className="h-5 w-5 text-status-active-text"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg
          className="h-5 w-5 text-status-rejected-text"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
    </span>
  );
}

function DownlinesSkeleton() {
  return (
    <div className="space-y-8" role="status" aria-busy="true">
      <LoadingStatus label="Loading downlines" />
      <div className="border-b-2 border-border-default pb-7">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="mt-3 h-12 w-72 max-w-full" />
        <SkeletonText width="w-full max-w-xl" className="mt-4" />
      </div>
      <Skeleton className="h-[250px] w-full rounded-card" />
      <Card padding="none" className="overflow-hidden border-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="m-4 h-16 w-[calc(100%-2rem)] rounded-card" />
        ))}
      </Card>
    </div>
  );
}

function ContractCount({ stats }: { stats: DownlineRow["contractStats"] }) {
  if (stats.total === 0) {
    return <span className="text-content-muted">0</span>;
  }
  return (
    <>
      <span className="font-semibold text-content-primary">{stats.total}</span>
      <span className="ml-1.5 text-xs text-content-muted">
        {stats.active} active
      </span>
    </>
  );
}

function ContractSummary({ stats }: { stats: DownlineRow["contractStats"] }) {
  if (stats.total === 0) {
    return <span className="text-xs text-content-muted">No contracts</span>;
  }
  const dominant =
    stats.nigo > 0
      ? "NIGO"
      : stats.pending > 0
        ? "PENDING"
        : stats.active > 0
          ? "ACTIVE"
          : stats.rejected > 0
            ? "REJECTED"
            : "PENDING";

  return <StatusBadge status={dominant} size="sm" />;
}

/**
 * A downline row opens that agent's contracts. The name is a real link so
 * keyboard and middle-click work; the row click is a convenience on top of it.
 */
function downlineContractsPath(downlineId: string) {
  return `/my-agent/downlines/${downlineId}`;
}

export function DownlinesList() {
  const token = getSessionToken();
  const navigate = useNavigate();
  const { agent } = useSession();
  const [downlines, setDownlines] = useState<DownlineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/agents/me/downlines`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || "Failed to load downlines");
        setDownlines(json.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load downlines");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  if (loading) return <DownlinesSkeleton />;

  return (
    <div className="space-y-8">
      <PortalPageIntro
        title="My downlines"
        action={
          <Link
            to="/my-agent/change-request"
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-accent-strong bg-accent-strong px-6 py-3 text-sm font-bold text-on-accent hover:brightness-95"
          >
            New change request <span aria-hidden>→</span>
          </Link>
        }
      />

      {agent?.id && (
        <ReferralLinksPanel
          ownerUserId={agent.managedAgentId ?? agent.id}
          ownerOptions={[
            {
              id: agent.managedAgentId ?? agent.id,
              label: "Me",
              commissionLevel: agent.commissionLevel ?? null,
            },
            ...downlines.map((downline) => ({
              id: downline.id,
              label: downline.displayName || downline.email,
              commissionLevel: downline.commissionLevel,
            })),
          ]}
        />
      )}

      {error && (
        <Card role="alert" className="border-2 border-status-rejected-border bg-status-rejected-bg text-status-rejected-text text-sm">
          {error}
        </Card>
      )}

      {!error && downlines.length === 0 && (
        <EmptyState
          title="No downline agents yet"
          description="Create a registration link above and share it to onboard agents under you."
        />
      )}

      {!error && downlines.length > 0 && (
        <Card padding="none" className="relative overflow-hidden border-2">
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-44 w-44 text-accent opacity-[0.035]" aria-hidden>
            <PortalWatermarkIcon kind="people" />
          </div>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-content-muted border-b-2 border-border-default bg-surface-overlay">
                  <th className="py-4 px-5 font-bold">Agent</th>
                  <th className="py-4 px-5 font-bold">Commission</th>
                  <th className="py-4 px-5 font-bold">Contracts</th>
                  <th className="py-4 px-5 font-bold">Status</th>
                  <th className="py-4 px-5 font-bold">Applied</th>
                  <th className="py-4 px-5 font-bold text-center">Registered</th>
                  <th className="py-4 px-5 font-bold"><span className="sr-only">View contracts</span></th>
                </tr>
              </thead>
              <tbody>
                {downlines.map((downline) => (
                  <tr
                    key={downline.id}
                    className="cursor-pointer border-b border-border-subtle last:border-0 hover:bg-surface-overlay/50"
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest("a")) return;
                      navigate(downlineContractsPath(downline.id));
                    }}
                  >
                    <td className="py-4 px-5">
                      <Link
                        to={downlineContractsPath(downline.id)}
                        className="focus-ring rounded font-medium text-content-primary hover:text-accent"
                      >
                        {downline.displayName || downline.email}
                      </Link>
                      {downline.displayName && (
                        <p className="text-xs text-content-muted mt-0.5">{downline.email}</p>
                      )}
                    </td>
                    <td className="py-4 px-5 text-content-secondary">
                      {downline.commissionLevel ?? <span className="text-content-muted">—</span>}
                    </td>
                    <td className="py-4 px-5 whitespace-nowrap">
                      <ContractCount stats={downline.contractStats} />
                    </td>
                    <td className="py-4 px-5">
                      <ContractSummary stats={downline.contractStats} />
                    </td>
                    <td className="py-4 px-5 text-content-secondary">
                      {new Date(downline.joinedAt).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-5 text-center">
                      <RegisteredIndicator registered={downline.isRegistered} />
                    </td>
                    <td className="py-4 px-5 text-right text-content-muted" aria-hidden>
                      <svg className="ml-auto h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m9 5 7 7-7 7" />
                      </svg>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card stack */}
          <div className="sm:hidden divide-y divide-border-subtle">
            {downlines.map((downline) => (
              <Link
                key={downline.id}
                to={downlineContractsPath(downline.id)}
                className="focus-ring block p-4 hover:bg-surface-overlay/50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-content-primary truncate">
                      {downline.displayName || downline.email}
                    </p>
                    <p className="text-xs text-content-muted mt-0.5 truncate">{downline.email}</p>
                    <p className="text-xs text-content-muted mt-1">
                      Joined {new Date(downline.joinedAt).toLocaleDateString()}
                      {downline.commissionLevel ? ` • ${downline.commissionLevel}` : ""}
                      {` • ${downline.contractStats.total} contract${downline.contractStats.total === 1 ? "" : "s"}`}
                      {downline.contractStats.total > 0 ? ` (${downline.contractStats.active} active)` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <ContractSummary stats={downline.contractStats} />
                    <RegisteredIndicator registered={downline.isRegistered} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
